// Domain types for What's On.
// Event/profile shapes mirror the Supabase row types but use camelCase for app code,
// with helpers to convert between snake_case DB rows and camelCase app objects.

import type { Database } from './database.types';

export type UserRole = 'admin' | 'client';
export type UserTier = 'standard' | 'trusted';
export type AuthProvider = 'google' | 'twitter';
export type EventStatus = 'pending' | 'approved' | 'rejected';

export type EventCategory =
  | 'conference'
  | 'hackathon'
  | 'ama'
  | 'meetup'
  | 'workshop'
  | 'community'
  | 'airdrop'
  | 'token_launch'
  | 'mainnet'
  | 'testnet'
  | 'governance'
  | 'deadline'
  | 'partnership'
  | 'other';

export type EventSource = 'admin' | 'client' | 'luma' | 'coinmarketcal' | 'messari' | 'manual';

export type ChainValue =
  | 'Solana'
  | 'General Crypto'
  | 'Ethereum'
  | 'Bitcoin'
  | 'Base'
  | 'Arbitrum'
  | 'Polygon'
  | 'Fogo';

export interface UserProfile {
  uid: string;
  email: string | null;
  role: UserRole;
  isApproved: boolean;
  companyName: string;
  displayName: string;
  authProvider: AuthProvider;
  twitterHandle: string | null;
  tier: UserTier;
  createdAt: Date;
  approvedAt?: Date | null;
}

export interface CryptoEvent {
  id: string;
  title: string;
  description?: string;
  /** Event start (used as the calendar slot). */
  date: Date;
  endDate?: Date | null;
  /** "HH:MM" 24h, "All day", or undefined. */
  time?: string;
  location?: string;
  category: EventCategory;
  status: EventStatus;
  rejectionReason?: string;
  createdBy: string | null;
  submitterName?: string;
  companyName?: string;
  /** Original target URL — the user-facing tracked URL is derived from `linkSlug`. */
  link?: string;
  linkSlug?: string | null;
  imageUrl?: string | null;
  ogImageUrl?: string | null;
  chain: ChainValue;
  featured: boolean;
  source: EventSource;
  externalId?: string | null;
  createdAt: Date;
}

// ---------------------------------------------------------------------------
// Mappers between snake_case DB rows and camelCase domain objects.
// ---------------------------------------------------------------------------

type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type EventRow   = Database['public']['Tables']['events']['Row'];

export function rowToProfile(row: ProfileRow): UserProfile {
  return {
    uid: row.id,
    email: row.email,
    role: row.role,
    isApproved: row.is_approved,
    companyName: row.company_name,
    displayName: row.display_name,
    authProvider: row.auth_provider,
    twitterHandle: row.twitter_handle,
    tier: row.tier,
    createdAt: new Date(row.created_at),
    approvedAt: row.approved_at ? new Date(row.approved_at) : null,
  };
}

export function rowToEvent(row: EventRow): CryptoEvent {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? undefined,
    date: new Date(row.starts_at),
    endDate: row.ends_at ? new Date(row.ends_at) : null,
    time: row.time_label ?? undefined,
    location: row.location ?? undefined,
    category: row.category,
    status: row.status,
    rejectionReason: row.rejection_reason ?? undefined,
    createdBy: row.created_by,
    companyName: row.company_name ?? undefined,
    link: row.link_url ?? undefined,
    linkSlug: row.link_slug,
    imageUrl: row.image_url,
    ogImageUrl: row.og_image_url,
    chain: row.chain,
    featured: row.featured,
    source: row.source,
    externalId: row.external_id,
    createdAt: new Date(row.created_at),
  };
}

// ---------------------------------------------------------------------------
// Category metadata
// ---------------------------------------------------------------------------

export interface CategoryMeta {
  value: EventCategory;
  label: string;
  fill: string;
  textColor: 'ink' | 'white';
}

export const CATEGORY_META: CategoryMeta[] = [
  { value: 'conference',    label: 'Conference',    fill: '#f59e0b', textColor: 'ink'   }, // honey amber
  { value: 'hackathon',     label: 'Hackathon',     fill: '#9945ff', textColor: 'white' }, // Solana purple
  { value: 'ama',           label: 'AMA',           fill: '#ec4899', textColor: 'white' }, // hot pink
  { value: 'meetup',        label: 'Meetup',        fill: '#14f195', textColor: 'ink'   }, // Solana green
  { value: 'workshop',      label: 'Workshop',      fill: '#3b82f6', textColor: 'white' }, // royal blue
  { value: 'community',     label: 'Community',     fill: '#f97316', textColor: 'white' }, // orange
  { value: 'airdrop',       label: 'Airdrop',       fill: '#fde047', textColor: 'ink'   }, // lemon yellow
  { value: 'token_launch',  label: 'Token Launch',  fill: '#06b6d4', textColor: 'ink'   }, // cyan/teal
  { value: 'mainnet',       label: 'Mainnet',       fill: '#0b0b0c', textColor: 'white' }, // ink black
  { value: 'testnet',       label: 'Testnet',       fill: '#cbd5e1', textColor: 'ink'   }, // light slate
  { value: 'governance',    label: 'Governance',    fill: '#84cc16', textColor: 'ink'   }, // lime
  { value: 'deadline',      label: 'Deadline',      fill: '#dc2626', textColor: 'white' }, // crimson red
  { value: 'partnership',   label: 'Partnership',   fill: '#d8b4fe', textColor: 'ink'   }, // lavender
  { value: 'other',         label: 'Other',         fill: '#f1f5f9', textColor: 'ink'   }, // near-white
];

export const CATEGORY_BY_VALUE: Record<EventCategory, CategoryMeta> = Object.fromEntries(
  CATEGORY_META.map((c) => [c.value, c]),
) as Record<EventCategory, CategoryMeta>;

const SOURCE_LABELS: Record<EventSource, string> = {
  admin: 'Admin',
  client: 'Client',
  luma: 'Luma',
  coinmarketcal: 'CoinMarketCal',
  messari: 'Messari',
  manual: 'Imported',
};

/**
 * Resolve who/what to display as the submitter for an event.
 * - If a real user submitted it, prefer their nickname/company.
 * - For automated imports (no created_by), fall back to the source name.
 * - For events whose author was deleted, "Deleted user".
 */
export function submitterLabel(
  event: Pick<CryptoEvent, 'createdBy' | 'submitterName' | 'source'>,
  submitter?: UserProfile,
): string {
  if (submitter) return submitter.displayName || submitter.companyName;
  if (event.submitterName) return event.submitterName;
  if (event.createdBy === 'deleted_user') return 'Deleted user';
  if (!event.createdBy) return SOURCE_LABELS[event.source] ?? 'Imported';
  return 'Unknown';
}
